// Measuring one mark. A mark sits in one of two places: on the box the renderer actually drew — an
// element node wears its id on its own `<div>` — or on a boxless wrapper around what a theme drew, which
// is how a section is marked so that no theme's grid, flex or flow ever sees an extra child. Both are
// measured here, so nothing above this file has to know which kind of mark it is holding.

// Measures the union of what one mark drew, for a mark that has no box of its own.
const childrenRect = (host: Element): DOMRect | null => {
  const boxes = Array.from(host.children).map((child) => child.getBoundingClientRect()).filter((box) => box.width > 0);
  if (boxes.length === 0) return null;
  const left = Math.min(...boxes.map((box) => box.left));
  const top = Math.min(...boxes.map((box) => box.top));
  const right = Math.max(...boxes.map((box) => box.right));
  const bottom = Math.max(...boxes.map((box) => box.bottom));
  return new DOMRect(left, top, right - left, bottom - top);
};

/**
 * Measures the box one mark occupies. A `display: contents` wrapper reports an empty rect of its own, so
 * an empty measurement is what sends this to the markup inside it — cheaper, and steadier under a drag,
 * than asking for the computed display of every mark on the page sixty times a second.
 */
export function markRect(mark: Element | null): DOMRect | null {
  if (!mark) return null;
  const own = mark.getBoundingClientRect();
  if (own.width > 0 || own.height > 0) return own;
  return childrenRect(mark);
}
